import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, fromEvent } from 'rxjs';
import {
  auditTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
} from 'rxjs/operators';

/**
 * Directive that emits a `nearEnd` event when the user scrolls past a
 * configurable percentage threshold of a scrollable element.
 *
 * Designed for plain `[dataSource]` mat-tables where `*cdkVirtualFor` is
 * absent and `renderedRangeStream` never emits. Drives the pipeline from
 * the native DOM scroll event instead.
 *
 * Usage:
 * ```html
 * <div
 *   appLazyScroll
 *   [threshold]="60"
 *   [isLoading]="isLoading"
 *   [hasMorePages]="pageNumber < totalPages"
 *   [resetTrigger]="searchResetCounter"
 *   (nearEnd)="handleLazyLoad()">
 * </div>
 * ```
 */
@Directive({
  selector: '[appLazyScroll]',
  standalone: true,
})
export class LazyScrollDirective implements OnInit {

  /**
   * Scroll percentage (0-100) at which `nearEnd` fires.
   * Defaults to 60 so the next page fetch begins before the user
   * reaches the very end of the list.
   */
  @Input() threshold = 60;

  /**
   * Whether an API call is currently in-flight.
   * When `true` the entire pipeline is skipped, preventing duplicate
   * requests while the previous page is still loading.
   */
  @Input() isLoading = false;

  /**
   * Whether more pages are available to fetch.
   * When `false` the threshold filter blocks emission entirely,
   * stopping lazy-load triggers once the last page has been reached.
   */
  @Input() hasMorePages = true;

  /**
   * Resets the scroll pipeline when its value changes.
   *
   * Bind to a counter that increments on every new search or sort -
   * e.g. `[resetTrigger]="searchResetCounter"`. Each change emits on
   * `reset$`, causing `switchMap` to tear down the current inner
   * subscription and create a fresh one with a clean `distinctUntilChanged`
   * state, so `nearEnd` can fire again from the top of the new result set.
   *
   * Using a dedicated counter rather than `pageNumber` ensures the setter
   * always fires even when `pageNumber` resets to the same value (1 to 1).
   */
  @Input() set resetTrigger(_: unknown) {
    this.reset$.next();
  }

  /** Emits once each time the user scrolls past the threshold percentage. */
  @Output() nearEnd = new EventEmitter<void>();

  private readonly el = inject(ElementRef<HTMLElement>);

  /** Completes all subscriptions when the directive is destroyed. */
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Emitted whenever the scroll pipeline should restart.
   * Triggered by the `resetTrigger` setter.
   */
  private readonly reset$ = new Subject<void>();

  ngOnInit(): void {
    const el = this.el.nativeElement;

    this.reset$.pipe(
      // Start the inner pipeline immediately on init without waiting for
      // the first `resetTrigger` input change.
      startWith(null),

      // Each emission from reset$ tears down the previous inner subscription
      // and creates a new one - giving each search its own fresh
      // `distinctUntilChanged` instance with no memory of prior positions.
      switchMap(() =>
        fromEvent(el, 'scroll').pipe(

          // Coalesce rapid scroll events into one calculation per 200 ms
          // to avoid thrashing during fast scrolling.
          auditTime(200),

          // Guard checked AFTER auditTime so it reads the value after Angular
          // has propagated the isLoading input change from the parent.
          // Checking before auditTime would allow events through that started
          // the 200ms window when isLoading was false but became true mid-wait.
          filter(() => !this.isLoading),

          // Calculate how far through the scrollable content the user is:
          // (scrollTop + clientHeight) / scrollHeight * 100
          map(() => {
            const scrolled = el.scrollTop + el.clientHeight;
            const total    = el.scrollHeight;
            return total > 0 ? (scrolled / total) * 100 : 0;
          }),

          // Convert the changing percentage to threshold state before applying
          // distinctUntilChanged. Comparing raw percentages caused another
          // emission for every scroll event while the user remained above the
          // threshold.
          map((percent) => percent >= this.threshold),
          distinctUntilChanged(),

          // Emit only when crossing from below to above the threshold and when
          // another page is available.
          filter((hasCrossedThreshold) => hasCrossedThreshold && this.hasMorePages),
        )
      ),

      takeUntilDestroyed(this.destroyRef),
    )
    .subscribe(() => this.nearEnd.emit());
  }
}
